<template>
  <div v-if="question" class="grid h-full grid-cols-1 grid-rows-[auto_1fr]">
    <!-- Header -->
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="isHorizontalLayout"
      :title="`${questionnaire?.title} - ${modifiedQuestion?.title}`"
      navigate-to-previous-page
      :is-modified="isModified"
      :mode="mode"
      :is-edit-active="question.isFromManager || false"
      @edit-mode="onEditMode"
      @cancel="onCancelEdit"
      @save="onSaveChanges"
      @horizontal-layout="(value: boolean) => (isHorizontalLayout = value)" />

    <AppResizableContainer
      :layout="isHorizontalLayout ? 'vertical' : 'horizontal'"
      :minSize="150"
      class="overflow-auto">
      <template #headerContent>
        <!-- Horizontal Layout -->
        <template v-if="isHorizontalLayout">
          <QuestionDetails
            v-model="modifiedQuestion"
            :question="question"
            :mode="mode"
            :isHorizontalLayout="isHorizontalLayout">
          </QuestionDetails>
        </template>

        <!-- Vertical Layout -->
        <template v-else>
          <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
            <QuestionDetails
              v-model="modifiedQuestion"
              :question="question"
              :mode="mode"
              :isHorizontalLayout="isHorizontalLayout">
            </QuestionDetails>
          </PerfectScrollbar>
        </template>
      </template>

      <!-- Tables Section -->
      <template #detailsTitle>
        <nav
          role="section-nav"
          class="flex justify-between px-6 text-sm font-medium text-center text-gray-500 sm:px-0 min-h-9">
          <ul class="flex flex-wrap items-center gap-4 pl-4 -mb-px">
            <li
              v-for="tab in tabs"
              :key="tab.name"
              @click="selectedTab = tab.name"
              class="py-1.5 cursor-pointer capitalize"
              :class="selectedTab === tab.name ? 'border-b-2 border-theme-500' : ''">
              <p>{{ tab.label }}</p>
            </li>
          </ul>
        </nav>
      </template>
      <template #detailsContent>
        <template v-if="selectedTab === 'listValues'">
          <QuestionListValues
            v-if="modifiedListRows && listRows && hasListRows"
            v-model="modifiedListRows"
            :mode="mode"
            :list-rows="listRows"
            :list-id="question.qstListId"
            :has-list-values="hasListRows"
            @delete="onDeleteListRows">
          </QuestionListValues>
          <BaseEmptyState v-else />
        </template>

        <template v-if="selectedTab === 'prototypes'">
          <template v-if="!hasPrototypes">
            <BaseEmptyState />
          </template>

          <template v-else>
            <template v-if="modifiedPrototype">
              <QuestionDetailsTablePrototypes
                :mode="mode"
                v-model="modifiedPrototype"
                :question="question" />
            </template>
            <template v-else>
              <LoadingScreen small />
            </template>
          </template>
        </template>
      </template>
    </AppResizableContainer>

    <BaseDrawerConfirm
      :is-active="confirmModalActive"
      @cancel="confirmModalActive = false"
      @confirm="onDiscardChanges" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import useVuelidate from '@vuelidate/core';
import { useI18n } from 'vue-i18n';
import { MWDataTypes, MWQuestionnaireTypes } from '@/utils/constants';
import type { PerfectScrollbarExpose } from 'vue3-perfect-scrollbar';
import { deepCopy } from '@/utils/data';
import {
  getQuestionById,
  getQuestionnaireById,
  addOrEditQuestion,
  getQuestionListRows,
  addOrEditQuestionList,
  addOrEditQuestionListRows,
  removeQuestionListRowsByIds,
  getPrototypeForQuestion,
  savePrototypeForQuestion,
} from '@/services/api/questionnaires';

import AppResizableContainer from '@/components/common/AppResizableContainer.vue';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import QuestionDetails from '@/components/questionnaires/QuestionDetails.vue';
import QuestionListValues from '@/components/questionnaires/QuestionListValues.vue';
import QuestionDetailsTablePrototypes from '@/components/questionnaires/QuestionDetailsTablePrototypes.vue';
import BaseEmptyState from '@/components/common/BaseEmptyState.vue';
import LoadingScreen from '@/components/common/LoadingScreen.vue';

import type {
  QstQuestionnairePrototype,
  QstListRow,
  Question,
  Questionnaire,
} from '@/types/Questionnaires';
import { RecordMode } from '@/types/Base';

const { t: $t } = useI18n();
const $v = useVuelidate();
const router = useRouter();
const route = useRoute();

const question = ref<Question>();
const modifiedQuestion = ref<Question>();

const questionnaire = ref<Questionnaire>();

const isHorizontalLayout = ref(true);
const mode = ref<'view' | 'edit'>('view');

const resetTablesState = ref(false);

const tabs = ref([
  {
    name: 'listValues',
    label: $t('question.listValues'),
  },
  {
    name: 'prototypes',
    label: $t('questionnaire.prototypes'),
  },
]);

const selectedTab = ref('listValues');

onMounted(async () => {
  await loadData();
});

const hasListRows = computed(() => {
  return (
    questionnaire.value?.mwQuestionnaireType === MWQuestionnaireTypes.Generic &&
    (modifiedQuestion.value?.mwDataType?.code === 9 ||
      modifiedQuestion.value?.mwDataType?.code === 10)
  );
});

const modifiedListRows = ref<QstListRow[]>();
const listRows = ref<QstListRow[]>([]);
async function loadData() {
  const qst = await getQuestionnaireById(route.params.id as string);
  if (qst) questionnaire.value = qst;

  const res = await getQuestionById(route.params.id as string, route.params.qid as string);
  if (res) {
    question.value = res as Question;

    mode.value = 'view';
  } else {
    question.value = {
      id: route.params.qid as string,
      isFromManager: true,
      isActive: true,
      title: '',
      description: '',
      qstQuestionnaireId: route.params.id as string,
      allowNull: false,
      mwDataTypeId: '',
      isIncludeLastVisitData: false,
    };
    mode.value = 'edit';
  }

  modifiedQuestion.value = deepCopy<Question>(question.value);

  if (hasListRows.value) {
    const rows = await getQuestionListRows(route.params.id as string, route.params.qid as string);
    if (rows) listRows.value = rows;
  } else listRows.value = [];

  modifiedListRows.value = deepCopy<QstListRow[]>(listRows.value);
  if (res && hasPrototypes.value) getPrototypeDataForQuestion();
}

const isModified = ref<boolean>(false);
const modifiedPrototype = ref<QstQuestionnairePrototype>();
const prototype = ref<QstQuestionnairePrototype>();
watchEffect(() => {
  isModified.value =
    JSON.stringify(modifiedQuestion.value) !== JSON.stringify(question.value) ||
    JSON.stringify(modifiedListRows.value) !== JSON.stringify(listRows.value) ||
    JSON.stringify(modifiedPrototype.value) !== JSON.stringify(prototype.value);
});

const confirmModalActive = ref(false);
const isBackMode = ref(false);

function onCancelEdit(backMode: string) {
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') {
      isBackMode.value = true;
    }
    return;
  } else if (!question.value?.createdDate) {
    router.go(-1);
  }
  mode.value = 'view';
}

async function onDiscardChanges() {
  mode.value = 'view';
  confirmModalActive.value = false;
  if (question.value) {
    modifiedQuestion.value = deepCopy<Question>(question.value);
  }
  if (listRows.value) {
    modifiedListRows.value = deepCopy<QstListRow[]>(listRows.value);
  }
  if (prototype.value) {
    modifiedPrototype.value = deepCopy<QstQuestionnairePrototype>(prototype.value);
  }
  resetTablesState.value = true;
  if (!question.value?.createdDate || isBackMode.value) {
    router.go(-1);
  }
}

const idsToDelete = ref<string[]>([]);

async function onSaveChanges() {
  if (!questionnaire.value || !modifiedQuestion.value) return;
  $v.value.$touch();
  if ($v.value.$invalid) return;
  mode.value = 'view';
  if (!isModified.value) return;

  if (idsToDelete.value.length && hasListRows.value) {
    await removeQuestionListRowsByIds(
      questionnaire.value.id,
      modifiedQuestion?.value.id,
      idsToDelete.value
    );
  }

  if (modifiedListRows.value && modifiedListRows.value.length && hasListRows.value) {
    await addOrEditQuestionList(questionnaire.value.id, modifiedQuestion?.value.id, {
      id: modifiedListRows.value[0].qstListId,
      multiSelect: modifiedQuestion.value.mwDataType?.code === 10,
      isFromManager: true,
      isActive: true,
    });

    await addOrEditQuestionListRows(
      questionnaire.value.id,
      modifiedQuestion?.value.id,
      modifiedListRows.value.filter((x) => x.modified)
    );
  }
  const {
    id,
    title,
    description,
    sort,
    image,
    mwDataTypeId,
    qstQuestionnaireId,
    allowNull,
    isIncludeLastVisitData,
    isActive,
    isFromManager,
    halo,
    lalo,
  } = modifiedQuestion?.value || {};

  const data = {
    id,
    title,
    description,
    sort,
    image,
    mwDataTypeId,
    qstQuestionnaireId,
    allowNull,
    isIncludeLastVisitData,
    isActive,
    isFromManager,
    halo,
    lalo,
  } as Question;

  if (modifiedListRows.value?.length) data.qstListId = modifiedListRows.value[0].qstListId;

  await addOrEditQuestion(questionnaire.value.id, data);

  savePrototype();

  await loadData();

  idsToDelete.value = [];
}
const scrollbarId = ref<PerfectScrollbarExpose>();

function onEditMode() {
  mode.value = 'edit';
  resetTablesState.value = false;
}

function onDeleteListRows(ids: string[]) {
  idsToDelete.value.push(...ids);
}

const allowedDataTypes = [
  MWDataTypes.INT,
  MWDataTypes.INTN,
  MWDataTypes.INTN0,
  MWDataTypes.FLOAT,
  MWDataTypes.FLOATN,
  MWDataTypes.FLOATN0,
  MWDataTypes.BOOLEAN,
] as const;
const hasPrototypes = computed(() => {
  const isQuestionnaireTypeValid =
    questionnaire.value &&
    (questionnaire.value.mwQuestionnaireType === MWQuestionnaireTypes.ForwardStock ||
      questionnaire.value.mwQuestionnaireType === MWQuestionnaireTypes.MerchandisingStandardAlt);

  const isDataTypeValid = question.value?.mwDataType?.name
    ? (allowedDataTypes as readonly string[]).includes(question.value.mwDataType.name)
    : false;

  return isQuestionnaireTypeValid && isDataTypeValid;
});

async function getPrototypeDataForQuestion() {
  if (!question?.value) return;
  if (!questionnaire?.value?.bindingId) return;

  const res = await getPrototypeForQuestion(
    question.value.id,
    questionnaire.value.bindingId,
    questionnaire.value.bindingType
  );
  if (res) {
    prototype.value = res;
    modifiedPrototype.value = deepCopy<QstQuestionnairePrototype>(prototype.value);
  }
}

async function savePrototype() {
  const updatedPrototypeData = modifiedPrototype.value?.qstQuestionnairePrototypeData.filter(
    (x) => x.recordMode == RecordMode.Modified
  );
  if (modifiedPrototype.value && updatedPrototypeData) {
    modifiedPrototype.value.qstQuestionnairePrototypeData = updatedPrototypeData;
    await savePrototypeForQuestion(modifiedPrototype.value);
  }
}
</script>
